// Today as a running timeline: what has been proposed, in the order it kicks off.
//
// Sets were a snapshot — "Set 2 of 3", arrows, a reshuffle — and a snapshot
// cannot answer the two questions a punter actually has: what have I already
// seen, and what is about to start. So the day is ONE list instead, ordered by
// the earliest kick-off on each ticket, with a fold at "now": scroll up for
// what has been and gone, down for what is still to come.
//
// Everything here is pure arithmetic over tickets that are already on the
// device. NOTHING IN THIS FILE FETCHES ANYTHING, and nothing that reads it may
// either: dealing, refilling and reclassifying are all computed from the board
// the app already paid for. That is the whole point of the design — a timeline
// that re-priced itself as matches kicked off would spend a credit every few
// minutes with nobody having touched anything.
//
// A leaf: `when.js` and `tickets.js` import nothing that reaches back here, so
// this runs in node with no store, no browser and no clock of its own.

import { legKickoff, whenParts } from './when.js';
import { canonicalStrategyId } from './engine/strategies/aliases.js';
import { TIER_ORDER } from './tickets.js';

/** How long a ticket reads NEW after it is dealt: two hours, the length of an afternoon's refills. */
export const NEW_FOR_MS = 2 * 60 * 60 * 1000;

/**
 * How long BEFORE the start a ticket stops being live: nothing at all.
 *
 * IT WAS FIFTEEN MINUTES, and the fifteen was the app deciding on a punter's
 * behalf that they had run out of time. A bookmaker takes the bet until the
 * market closes, which is the start — and the app cannot know whether the
 * reader has the book already open on the next screen. Offering a ticket that
 * cannot be placed is bad; withdrawing one that can be is worse, because
 * nothing on the screen says it happened.
 *
 * On a CARD it was not a judgement call, it was a bug. A fixture list has one
 * match an hour and fifteen minutes off the front of it costs nothing; a card
 * jumps a race every two or three minutes, so a racing ticket was born expired
 * and the whole strategy read as broken. That is what this number was actually
 * doing to the feed.
 *
 * Kept as a constant, at zero, because the IDEA is still real — a leg under way
 * cannot be backed — and it is `raceStarted` and the exact start that say so
 * now. A future rule that wants a grace period has somewhere to put it.
 */
export const LIVE_BUFFER_MS = 0;

/** The slot a hand-built ticket occupies: its own, and nothing else's. */
export const BUILT_SLOT = 'built-by-hand';

/** Classic's own id, which is the slot key everything without one falls back to. */
export const CLASSIC_SLOT_ID = 'classic-four-ways';

/**
 * WHICH GROUP A TICKET BELONGS TO — and a group is no longer a quota.
 *
 * THERE WERE CAPS HERE AND THERE ARE NOT NOW. `TIMELINE_CAPS` held two live
 * tickets per Classic design and one per other strategy, and every automatic
 * deal filled those quotas and then stopped. The owner read the result as the
 * app having run out of things to say: "just propose another set of bets that
 * haven't been proposed before." A quota is the wrong shape for that. What the
 * automatic deals hold to now is a VOLUME per deal event and per day
 * (`AUTO_DEAL_TARGET`, `AUTO_DEAL_DAILY` in store.js), and what stops them
 * repeating themselves is `variesFrom` — the same rule a punter's own tap has
 * always been held to.
 *
 * The slot key survives because two other things need it: the name a deal
 * prints in a toast (`deal-copy.js`'s `slotName`), and the grouping `variesFrom`
 * is judged within — Classic's designs are subsets of each other BY DESIGN, so
 * a variety rule run across them would call the pad a repeat of itself.
 *
 * Canonicalised, so a ticket still on the timeline under a retired id groups
 * with the strategy it merged into.
 *
 * A hand-built ticket carries no strategy id, and without a slot of its own it
 * would fall through to Classic's — where the variety rule would judge a
 * punter's own two-leg multi against the pad's own two-leg ticket.
 */
export function slotOf(multi) {
  if (multi?.builtByHand) return BUILT_SLOT;
  const id = canonicalStrategyId(multi?.strategyId) ?? CLASSIC_SLOT_ID;
  // THE DESIGN, NOT THE RISK PRINTED ON IT. Classic's slot is one of its four
  // ways — the leg count it was cut to — and that is a fixed set of four. The
  // tier is read off the combined chance now (lib/tier.js) and two of the four
  // designs can easily land in one band, so a key on the word would group two
  // Mediums together and have the variety rule call one of them a repeat.
  // `design ?? tier` reads a ticket dealt before the split as what it was.
  return id === CLASSIC_SLOT_ID ? `${id}#${multi?.design ?? multi?.tier ?? 'none'}` : id;
}

/** One leg's kick-off as epoch ms, or null where it cannot be read. */
const startOf = (leg) => {
  const at = legKickoff(leg);
  const ms = at instanceof Date ? at.getTime() : typeof at === 'number' ? at : Date.parse(at ?? '');
  return Number.isFinite(ms) ? ms : null;
};

/** Every kick-off on a ticket, as epoch ms, unreadable ones dropped. */
const kickoffs = (legs) => (legs ?? []).map(startOf).filter((ms) => ms !== null);

/**
 * Has THIS leg started? The one definition, shared by the timeline and by every
 * screen that strikes a started leg through: the card's own word for it where
 * there is one (`raceStarted`), and otherwise the clock against the scheduled
 * start, to the second.
 */
export function legStarted(leg, now = Date.now()) {
  if (raceStarted(leg, now)) return true;
  const at = startOf(leg);
  return at !== null && at <= now;
}

/**
 * A ticket's cutoff: the EARLIEST kick-off on it, which is the moment the whole
 * multi stops being placeable. Null when no leg says when it starts — a void
 * tier has no legs at all, and a ticket the app cannot time is not a ticket it
 * may expire.
 */
export function ticketCutoff(legs) {
  const list = kickoffs(legs);
  return list.length ? Math.min(...list) : null;
}

/** The LAST kick-off on a ticket — the "allow started" rule's own horizon. */
export function ticketLastStart(legs) {
  const list = kickoffs(legs);
  return list.length ? Math.max(...list) : null;
}

/**
 * HAS THE RACE THIS LEG IS ON ALREADY JUMPED — according to the card, not to
 * the clock?
 *
 * A fixture starts when the fixture list says it starts, give or take a coin
 * toss. A race does not: it goes when the starter sends them, which on a busy
 * Saturday is minutes either side of the card, and the one thing every
 * bookmaker does at that moment is close the market. PuntersEdge reports both
 * — `inplay_at` when the race is running and `market_closed_at` when betting
 * shut — and a ticket offered on a race the books have already closed is worse
 * than no ticket: it reads as the app not knowing what is happening.
 *
 * So where the card has said it, the card wins. A stamp in the FUTURE is the
 * card saying "not yet" and is left alone; a stamp that cannot be read at all
 * is taken as said, because the feed only writes these fields when the thing
 * has happened.
 */
export function raceStarted(leg, now = Date.now()) {
  for (const stamp of [leg?.inplayAt, leg?.marketClosedAt]) {
    if (stamp === null || stamp === undefined || stamp === '') continue;
    const at = Date.parse(stamp);
    if (!Number.isFinite(at) || at <= now) return true;
  }
  return false;
}

/**
 * What a ticket is right now: 'live' · 'placed' · 'expired' · 'resulted'.
 *
 * `placed` is the bet record for this multi, or null. A ticket somebody backed
 * is PLACED until the bet settles and RESULTED after — its kick-off stops
 * mattering the moment money is on it, which is why neither of those states
 * consults the clock.
 *
 * Otherwise it is live until its first leg STARTS — to the second, with no
 * buffer in front of it (`LIVE_BUFFER_MS`) — or, when the profile allows a
 * started leg to be rung through, until the LAST leg has started, because after
 * that there is nothing left to ring through.
 */
export function ticketState(ticket, { now = Date.now(), placed = null, allowStarted = false } = {}) {
  if (placed) {
    const result = placed.result ?? null;
    return result && result !== 'pending' ? 'resulted' : 'placed';
  }
  const legs = ticket?.legs ?? [];
  // ONE JUMPED LEG EXPIRES THE WHOLE TICKET, whatever `allowStarted` says and
  // whatever the clock says. `allowStarted` is the punter's offer to phone a
  // started leg through, and a race whose market has closed cannot be phoned
  // through to anybody.
  if (legs.some((leg) => raceStarted(leg, now))) return 'expired';
  const cutoff = ticketCutoff(legs);
  // Nothing to expire against. A void tier is not a proposal that ran out of
  // time; it is one the board could never fill.
  if (cutoff === null) return 'live';
  if (allowStarted) {
    const last = ticketLastStart(legs);
    return last !== null && last > now ? 'live' : 'expired';
  }
  return cutoff > now + LIVE_BUFFER_MS ? 'live' : 'expired';
}

/**
 * What makes two tickets the same ticket: the strategy, the tier, and the legs
 * by match and side — not the price, which a later board may move, and not
 * the id, which every deal mints afresh. The paper trail keys the same way
 * (`proposalKey` in lib/proposals.js), so a ticket that is one line there is
 * one line here.
 *
 * A TICKET THE PUNTER EDITED IS ITS OWN TICKET, whatever it now looks like.
 * `derivedFrom` (`lib/engine/reprice.js`) joins the key for exactly that
 * reason: drop the long leg off a three-leg Classic ticket and what is left can
 * be the very two favourites the Low ticket is already printing, at the same
 * tier — and keyed on the legs alone the edit would be deduped away against a
 * ticket dealt earlier, so the screen somebody was reading would go blank. The
 * dedupe still does its job for everything the ENGINE deals, which is what it
 * was built for.
 */
export function ticketKey(ticket) {
  const m = ticket?.multi ?? {};
  // The fields BOTH copies of a leg carry. A leg straight off the engine has
  // an `eventId` and a `selection` and nothing else; the same leg frozen at
  // placement has those too, plus a `matchKey` and a `side` the engine never
  // wrote. Keyed on the frozen-only fields, a placed ticket and its own dealt
  // copy read as two tickets — which is how a bet placed at $5 grew a $10
  // twin beside it. `matchKey`/`side` remain the fallback for a trail leg
  // that has no event id.
  const legs = [...legSetOf(ticket)].sort();
  // The DESIGN, not the risk word: the tier is read off the combined chance
  // now, so a ticket frozen at placement and the same legs re-dealt an hour
  // later at a moved price could carry two different words — and keyed on the
  // word, one ticket would print as two. `design ?? tier` reads a ticket dealt
  // before the two were split as exactly what it was.
  return `${m.strategyId ?? ''}|${m.design ?? m.tier ?? ''}|${m.derivedFrom ?? ''}|${legs.join(',')}`;
}

/**
 * A ticket's legs as a SET of `match:side` strings — the same spelling of a leg
 * `ticketKey` is built from, written once so the two can never come to
 * disagree about what one is.
 *
 * The fields BOTH copies of a leg carry: a leg straight off the engine has an
 * `eventId` and a `selection`; the same leg frozen at placement has those plus
 * a `matchKey` and a `side` the engine never wrote.
 */
export function legSetOf(ticket) {
  return new Set((ticket?.legs ?? [])
    .map((l) => `${l.eventId ?? l.matchKey ?? ''}:${l.selection ?? l.side ?? ''}`));
}

/**
 * Is this ticket genuinely DIFFERENT from every one already dealt beside it?
 *
 * NEITHER INSIDE NOR AROUND. Two tickets with the same legs are the same
 * ticket and `ticketKey` catches those on its own; what this adds is that the
 * same three favourites with a fourth bolted on — or with one taken off — is
 * the ticket somebody is already holding, edited. It is not a second opinion
 * about the day, and a punter who asked for a different multi has not been
 * given one. Two tickets sharing two legs of three ARE different: each has
 * something the other has not, which is what makes it a choice.
 *
 * WITHIN ONE SLOT ONLY, and that is the caller's grouping to make (`slotOf`).
 * Classic's tiers are subsets of each other BY DESIGN — Low is two favourites
 * and High is four, often the same two among them — and that is the shape of
 * the pad rather than a repeat of it.
 *
 * `existing` is a list of `legSetOf` sets; an empty one means anything goes.
 */
export function variesFrom(existing, set) {
  if (!set?.size) return false;
  for (const other of existing ?? []) {
    const small = set.size <= (other?.size ?? 0) ? set : other;
    const large = small === set ? other : set;
    let inside = true;
    for (const leg of small) {
      if (!large.has(leg)) { inside = false; break; }
    }
    if (inside) return false;
  }
  return true;
}

/**
 * The day's proposals as one ordered list, and where "Now" sits in it.
 *
 * @param {Array} tickets  `{ multi, legs, pick?, strategy?, dealtAt?, pricesFrom? }`,
 *                         in the order they were dealt
 * @param {object} opts
 * @param {number} opts.now            epoch ms — the store's ticking clock
 * @param {Map}    opts.placedBy       multi.id → bet record, for the placed states
 * @param {boolean} opts.allowStarted  the risk profile's own rule
 * @param {Array}  opts.strategyOrder  strategy ids, in the order Today ranks them
 * @returns {{ entries: Array, foldIndex: number }}
 *   entries: `{ id, ticket, strategy, state, cutoff, dealtAt, pricesFrom }`
 *   foldIndex: where the Now rule goes. `entries.length` when nothing is still
 *   to come, so the rule lands at the foot rather than vanishing.
 *
 * THE FOLD IS A TIME, NOT A STATE. It is the first entry the CLOCK has not
 * passed — which for an unplaced ticket is the same thing as the first live
 * one, and for a placed ticket is the difference between a sane screen and a
 * silly one: backing the 7:20 must not send the ticket above a rule labelled
 * Now while the match it is on has not started. It also keeps a kick-off
 * cluster whole, since every ticket in one is on the same side of the clock —
 * which is what lets the rule sit BETWEEN clusters rather than cutting one in
 * half.
 */
export function timelineOf(tickets, {
  now = Date.now(),
  placedBy = null,
  allowStarted = false,
  strategyOrder = [],
} = {}) {
  const order = new Map(strategyOrder.map((id, i) => [id, i]));
  const rank = (id) => (order.has(id) ? order.get(id) : order.size + 1);
  // Classic's four ways, in the order the pad prints them — by DESIGN, which
  // is the leg count, not by the risk word read off the chance. Two designs
  // landing in one band must still print shortest ticket first.
  const designRank = (multi) => {
    const i = TIER_ORDER.indexOf(multi?.design ?? multi?.tier);
    return i === -1 ? TIER_ORDER.length : i;
  };

  // ONE TICKET IS ONE SET OF LEGS, whatever id the deal minted for it. A
  // multi's id changes with the seed, so a refill that lands on the same legs
  // as a ticket dealt two hours earlier — likely on a small board — would print
  // twice, and a placed ticket would grow an unplaced twin beside it. Keyed on
  // content instead; where two copies share a key the PLACED one is the record
  // and otherwise the earliest dealt, since that is the one somebody has seen.
  const byKey = new Map();
  for (const ticket of tickets ?? []) {
    const id = ticket?.multi?.id;
    if (id == null) continue;
    const key = ticketKey(ticket);
    const placed = placedBy?.get?.(id) ?? null;
    const prior = byKey.get(key);
    if (prior && (prior.placed || !placed)) continue;
    byKey.set(key, { ticket, placed });
  }
  const entries = [];
  for (const { ticket, placed } of byKey.values()) {
    const id = ticket.multi.id;
    entries.push({
      id,
      ticket,
      strategy: ticket.strategy ?? null,
      state: ticketState(ticket, { now, placed, allowStarted }),
      // The same test with the bet taken out of it: is this ticket still ahead
      // of the clock? It is the fold, and nothing else reads it.
      ahead: ticketState(ticket, { now, allowStarted }) === 'live',
      cutoff: ticketCutoff(ticket.legs),
      dealtAt: ticket.dealtAt ?? null,
      pricesFrom: ticket.pricesFrom ?? null,
      // How the bet on it went, once one is settled: the feed stamps WON /
      // LOST / VOID from this rather than PLACED for ever. Null until then.
      result: placed && placed.result && placed.result !== 'pending' ? placed.result : null,
    });
  }

  // Kick-off first, because the list IS a clock. A ticket with no kick-off to
  // read sorts to the foot rather than to the top: it is the one thing on the
  // page that cannot be "next".
  entries.sort((a, b) => {
    const ac = a.cutoff ?? Infinity;
    const bc = b.cutoff ?? Infinity;
    if (ac !== bc) return ac - bc;
    const as = rank(a.ticket?.multi?.strategyId ?? a.strategy?.id);
    const bs = rank(b.ticket?.multi?.strategyId ?? b.strategy?.id);
    if (as !== bs) return as - bs;
    return designRank(a.ticket?.multi) - designRank(b.ticket?.multi);
  });

  const foldIndex = (() => {
    const i = entries.findIndex((e) => e.ahead);
    return i === -1 ? entries.length : i;
  })();

  return { entries, foldIndex };
}

/**
 * Which groups have dealt today and have NOTHING live left: the slot keys, in
 * the order the day first dealt into them.
 *
 * THIS IS A TRIGGER, NOT A QUOTA, and the difference is the whole of what
 * changed with the caps. It does not say how many tickets anything is owed —
 * the deal that follows is a plain "propose another set nobody has been offered
 * today", bounded by a volume per event and per day. What it answers is the one
 * question the minute clock has: has something the punter was holding expired,
 * so that there is anything worth dealing at all?
 *
 * Counted over the slots the day has ALREADY dealt into, never over every slot
 * the engine could theoretically fill — a strategy that printed nothing this
 * morning printed nothing for a reason.
 *
 * The punter's own pile is never in the list: nothing the engine deals lands in
 * it, so a hand-built ticket expiring would have the minute clock asking for a
 * deal that can never come.
 */
export function emptySlots(entries) {
  const live = new Map();
  for (const e of entries ?? []) {
    const slot = slotOf(e?.ticket?.multi);
    if (slot === BUILT_SLOT) continue;
    if (!live.has(slot)) live.set(slot, 0);
    if (e?.state === 'live') live.set(slot, live.get(slot) + 1);
  }
  const out = [];
  for (const [slot, n] of live) if (!n) out.push(slot);
  return out;
}

/**
 * The heading over a cluster of tickets: the clock time they all kick off at,
 * with the day on it when that day is not today's.
 *
 * `whenParts` answers 'started' for anything in the past, which is the right
 * answer beside a LEG and the wrong one over a group — every past cluster would
 * be headed the same word and the list would stop being a clock. So a kick-off
 * that has been and gone is read as of a moment before itself, which gives the
 * time it actually was.
 */
export function kickoffLabel(cutoff, now = Date.now()) {
  if (!Number.isFinite(cutoff)) return 'No start time';
  const parts = whenParts(cutoff, now);
  if (parts.state && parts.state !== 'started') return parts.text;
  return whenParts(cutoff, cutoff - 1000).text || 'No start time';
}

/**
 * Entries gathered into kick-off clusters, in timeline order:
 * `[{ key, label, cutoff, entries }]`.
 *
 * A cluster is one moment on the clock, so every ticket in it is on the same
 * side of "now" — which is what lets the Now rule sit BETWEEN clusters rather
 * than cutting one in half.
 */
export function clusterTimeline(entries, now = Date.now()) {
  const groups = [];
  let current = null;
  for (const e of entries ?? []) {
    const key = e.cutoff ?? 'none';
    if (!current || current.key !== key) {
      current = { key, cutoff: e.cutoff ?? null, label: kickoffLabel(e.cutoff, now), entries: [] };
      groups.push(current);
    }
    current.entries.push(e);
  }
  return groups;
}
